import React, { useEffect, useRef, useContext } from 'react';
import axiosConn from '../../axiosConn';
import AuthChooser from '../auth/AuthChooser';
import { AuthContext } from '../../context/AuthContext';

// PostCompletionScreen renders a completion summary using a fixed contract.
// It is UI-only: no side effects, no routing, no fetching.
export default function PostCompletionScreen({
  open = true,
  context,
  completionContext,
  onClose,
  onCashOut,
  onUseLocally,
  onShare,
  onDoAnother,
  onCreateNext,
  authProps = null,
  playerBalance = null,
}) {
  const ctx = completionContext || context;
  const awardFiredRef = useRef(false);
  const { player } = useContext(AuthContext) || {};
  const bearer = player?.token ? `Bearer ${player.token}` : null;

  useEffect(() => {
    if (!open || !ctx) return;
    if (awardFiredRef.current) return;
    if (!ctx?.entityType || !ctx?.entityId || !ctx?.role) return;
    awardFiredRef.current = true;
    try {
      axiosConn.post(
        'action-credit/award',
        {
          entityType: ctx.entityType,
          entityId: ctx.entityId,
          role: ctx.role,
        },
        {
          withCredentials: true,
          headers: bearer ? { Authorization: bearer } : undefined,
        },
      );
    } catch (err) {
      // non-blocking best effort; ignore errors
    }
  }, [open, ctx, bearer]);

  if (!open || !ctx) return null;

  const {
    entityType,
    role = 'tasker',
    amountUSD,
    amountJGK,
    title,
    counterpartyName,
  } = ctx;

  const header =
    title || (entityType === 'task' ? 'Task completed' : 'Completion recorded');

  const proofLineUSD =
    Number.isFinite(Number(amountUSD)) && amountUSD !== null
      ? `$${Number(amountUSD).toFixed(2)}`
      : null;
  const proofLineJGK =
    Number.isFinite(Number(amountJGK)) && amountJGK !== null
      ? `${Number(amountJGK)} JGK`
      : null;

  const identityLine = `You completed a local action${
    counterpartyName ? ` with ${counterpartyName}` : ''
  }.`;
  const walletLine =
    Number.isFinite(Number(playerBalance)) && Number(playerBalance) > 0
      ? `Balance: ${Number(playerBalance).toLocaleString('en-US')} JGK ($${(
          Number(playerBalance) / 1000
        ).toFixed(2)})`
      : null;

  const isCreator = role === 'creator';
  const isTasker = role === 'tasker';

  const shareHandler = () => {
    const payload =
      ctx?.sharePayload ||
      {
        entityId: ctx?.entityId || ctx?.taskId || ctx?.id,
        entityType: ctx?.entityType || 'task',
        amountUSD: amountUSD ?? null,
        amountJGK: amountJGK ?? null,
      };
    if (onShare) onShare(payload);
  };

  const renderButton = (
    label,
    handler,
    variant = 'primary',
    { hideIfMissing = false, allowNoop = false } = {},
  ) => {
    if (!handler && hideIfMissing) return null;
    const safeHandler = handler || (allowNoop ? () => {} : null);
    const disabled = !safeHandler;
    const styles = {
      primary: {
        background: disabled
          ? 'rgba(59,130,246,0.25)'
          : 'linear-gradient(90deg, #22d3ee, #06b6d4)',
        color: '#0b1220',
      },
      ghost: {
        background: 'rgba(148,163,184,0.12)',
        color: '#e5e7eb',
        border: '1px solid rgba(148,163,184,0.18)',
      },
    }[variant];
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => (safeHandler ? safeHandler() : undefined)}
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          border: 'none',
          fontWeight: 700,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          ...styles,
        }}
      >
        {label}
      </button>
    );
  };

  const renderActions = () => {
    if (ctx?.authRequired && authProps) {
      return (
        <AuthChooser
          headerText={authProps.headerText || 'Sign in to continue'}
          subText={authProps.subText}
          onPrimary={authProps.onPrimary}
          onPrimaryStart={authProps.onPrimaryStart}
          primaryDisabled={authProps.primaryDisabled}
          email={authProps.email}
          onEmailChange={authProps.onEmailChange}
          emailError={authProps.emailError}
          onEmailErrorClear={authProps.onEmailErrorClear}
          onSendMagicLink={authProps.onSendMagicLink}
          magicStatus={authProps.magicStatus}
        />
      );
    }

    if (isCreator) {
      return (
        <>
          {renderButton('Create another task', onCreateNext || onDoAnother, 'primary', {
            allowNoop: true,
          })}
          {renderButton('Share outcome', shareHandler, 'ghost', { allowNoop: true })}
          {renderButton('Use locally', onUseLocally, 'ghost', {
            hideIfMissing: true,
          })}
          {renderButton('Close', onClose, 'ghost', { allowNoop: true })}
        </>
      );
    }
    // tasker / provider default
    return (
      <>
        {renderButton('Earn again', onDoAnother || onCreateNext)}
        {renderButton('Cash out', onCashOut, 'ghost', { allowNoop: true })}
        {renderButton('Use locally', onUseLocally, 'ghost', {
          allowNoop: true,
        })}
        {renderButton('Share what you did', shareHandler, 'ghost', {
          allowNoop: true,
        })}
        {renderButton('Close', onClose, 'ghost', { allowNoop: true })}
      </>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,12,20,0.82)',
        backdropFilter: 'blur(10px)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          background: '#0f172a',
          border: '1px solid rgba(148,163,184,0.18)',
          boxShadow: '0 22px 50px rgba(0,0,0,0.45)',
          borderRadius: 20,
          padding: 28,
          color: '#e5e7eb',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
          {header}
        </div>
        <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 22 }}>
          {identityLine}
        </div>

        <div
          style={{
            marginBottom: 24,
            padding: 18,
            borderRadius: 14,
            background: 'rgba(34,211,238,0.08)',
            border: '1px solid rgba(34,211,238,0.25)',
          }}
        >
          <div style={{ fontSize: 15, color: '#a5b4fc', marginBottom: 6 }}>
            Proof of value
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
            {proofLineUSD || '—'}
          </div>
          <div style={{ fontSize: 15, color: '#94a3b8' }}>
            {proofLineJGK ? `(${proofLineJGK})` : 'Amount unavailable'}
          </div>
        </div>
        <div
          style={{
            fontSize: '0.9em',
            fontWeight: 400,
            opacity: 0.75,
            color: '#9fb6d1',
            marginBottom: walletLine ? 14 : 0,
          }}
        >
          Each completed action builds trust and earns real value.
        </div>
        {walletLine ? (
          <div
            style={{
              fontSize: '0.9em',
              fontWeight: 400,
              opacity: 0.75,
              color: '#9fb6d1',
              marginBottom: 16,
            }}
          >
            {walletLine}
          </div>
        ) : null}

        <div style={{ display: 'grid', gap: 10 }}>{renderActions()}</div>
      </div>
    </div>
  );
}
